import { Compass, HeartHandshake, Route, ShieldCheck, Sparkles, Users } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import { useEnquiry } from "@/components/EnquiryModal";
import { FAQ_GROUPS, IMG, WHY_US } from "@/lib/site";

const PILLARS = [
  { icon: Compass, title: "Who We Are", text: "D Travel Link is a travel agency based in Thane West, Maharashtra. We plan and book tailor-made holidays — international, domestic, honeymoons, family trips and group tours — with visa, flight and hotel assistance under one roof." },
  { icon: Route, title: "Our Approach", text: "We listen first. Your dates, budget, pace and interests shape the itinerary — not the other way around. Every quote is itemised and every inclusion is in writing before you pay a rupee." },
  { icon: ShieldCheck, title: "Customer Support", text: "From your first enquiry to your flight home, you have one dedicated point of contact — plus 24/7 on-trip support for anything unexpected." },
  { icon: Sparkles, title: "Customised Holidays", text: "No two travellers are alike, so no two D Travel Link itineraries are either. Tell us the occasion — honeymoon, anniversary, school break — and we design around it." },
  { icon: Users, title: "Group Expertise", text: "Fixed-departure group tours with experienced tour managers, Indian meals where it matters, and pricing that rewards travelling together." },
  { icon: HeartHandshake, title: "Honest Guidance", text: "If a season is wrong, a route is rushed or a hotel is overhyped — we say so. We would rather lose a booking than send you on the wrong trip." },
];

export default function About() {
  const { openEnquiry } = useEnquiry();
  return (
    <div data-testid="about-page">
      <SEO title="About Us" description="D Travel Link — a Thane-based travel agency crafting tailor-made international and India holidays, honeymoons, group tours and visa assistance. Linking Dreams to Destinations." />
      <PageHero image={IMG.traveler1} overline="Linking Dreams to Destinations" title="About D Travel Link"
                description="A travel agency from Thane West that treats your holiday like our own reputation rides on it — because it does." />
      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm" data-testid={`about-pillar-${i}`}>
                  <span className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center"><p.icon className="w-5 h-5 text-skybrand" /></span>
                  <h3 className="mt-4 font-heading font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Why Choose Us</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {WHY_US.map((w, i) => (
              <Reveal key={w.title} delay={(i % 4) * 0.05}>
                <div className="h-full rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                  <span className="font-display italic text-2xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-2 font-heading font-bold text-sm text-slate-900">{w.title}</h3>
                  <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{w.description}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 max-w-3xl mx-auto">
            <Reveal className="text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">More Questions, Answered</h2>
            </Reveal>
            <div className="mt-8 space-y-10">
              {FAQ_GROUPS.map((g) => (
                <Reveal key={g.title}>
                  <h3 className="font-heading font-bold text-royal text-sm tracking-[0.15em] uppercase">{g.title}</h3>
                  <div className="mt-2"><FaqAccordion faqs={g.faqs} testidPrefix={`faq-${g.title.toLowerCase().replace(/\s+/g, "-")}`} /></div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal className="mt-16 text-center">
            <button onClick={() => openEnquiry({})} data-testid="about-cta-button"
                    className="rounded-full bg-navy px-8 py-3.5 font-heading font-bold text-white hover:bg-royal transition-colors">
              Start Planning With Us
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
